import { formatTime, Tooltip } from "@xola/ui-kit";
import { ColDef, SetFilterValuesFuncParams, ValueFormatterParams, ValueGetterParams } from "ag-grid-enterprise";
import clsx from "clsx";
import { DateFormat, formatDate, isPartnerPurchase } from "common";
import { get } from "get-wild";
import { capitalize, concat, invert, isEmpty, round, startCase, union } from "lodash";
import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
    BookingEngineNamedSourceCopy,
    Coupon,
    DelegateRole,
    Experience,
    ExperiencePurchaseItem,
    Installation,
    Partner,
    Plugin,
    Product,
    Purchase,
    PurchaseItemObject,
    Source,
    SourceCopy,
    Tag,
    TicketRedemption,
    Transaction,
    TransactionItem,
    TransactionType,
    User,
} from "types";
import { Currency } from "../../Common/Currency";
import { NoTranslate } from "../../Common/NoTranslate";
import { DateRangeFilter } from "./CustomFilters/CustomDateRangeFilter";
import { ReportFilterValue } from "./ReportFilterState";
import { getItemForTransaction } from "./SuggestedReports";
import { useDeletedExperiences } from "@/hooks/resources/useDeletedExperiences";
import { useProducts } from "@/hooks/resources/useProduct";
import { useAutoFetchPlugins } from "@/hooks/usePlugins";
import { useResource } from "@/hooks/useResource";
import { isPartnerReconcileTransaction, transactionSources } from "@/models/Transaction";
import { getCollection } from "@/utils/getCollection";

// This is required because in the filter params there will be a "Select All" option provided by agSetFilter
const isSelectAll = (value: User | Product | Coupon | { id: string } | string) => {
    return typeof value === "string" && /select all/i.test(value);
};

export const ExperienceNameRenderer = ({ value }: { value: ReportFilterValue | ReportFilterValue[] }) => {
    const id = typeof value === "string" ? value : null;
    const { data: experience } = useResource<Experience>(["products", id], null, { enabled: !!id });
    return <span>{experience?.name}</span>;
};

export const UserRenderer = ({ value }: { value: User | string }) => {
    const id = typeof value === "string" ? value : value?.id;
    const { data: user } = useResource<User>(["users", id], null, { enabled: !!value && !isSelectAll(value) });
    return isSelectAll(value) ? value : <div>{user?.name ?? id ?? "N/A"}</div>;
};

export const SourceCellRenderer = ({ value }: { value: Source }) => {
    const { data: plugins = [] } = useAutoFetchPlugins();
    const plugin = plugins.find((plugin) => plugin.id === value);
    const source = plugin ? `Plugin: ${plugin?.name}` : SourceCopy[value] ?? startCase(value);
    return <span>{source}</span>;
};

export const StatusCellRenderer = ({ value }: { value: Source }) => {
    return <span>{startCase(value)}</span>;
};

export const ProductRenderer = ({ value }: { value: Product | { id: string } | string }) => {
    const id = typeof value === "string" ? value : value?.id;
    const { data: product } = useResource<Product>(["products", id], null, {
        enabled: !!value && !isSelectAll(value),
    });

    if (isSelectAll(value)) {
        return value;
    }

    if (product?.name) {
        return product.name;
    }

    if (typeof value === "string") {
        return value;
    }

    return value?.id ?? "";
};

const PurchaseRenderer = ({
    value,
    data,
}: {
    value: string;
    data: Transaction | Purchase | ExperiencePurchaseItem | TicketRedemption;
}) => {
    const history = useHistory();

    if (isSelectAll(value)) {
        return value;
    }

    const isTransaction = "type" in data;
    let isPartnerTransaction = false;
    const hasPartnerTransactionType = isTransaction && isPartnerReconcileTransaction(data);

    if (
        hasPartnerTransactionType ||
        ("purchase" in data && data.purchase && isPartnerPurchase(data.purchase) && !data.purchase.items)
    ) {
        isPartnerTransaction = true;
    }
    const handleClick = () => {
        const purchaseId = getPurchaseId(data);

        const itemId = isTransaction ? null : getPurchaseItemId(data);
        const url = itemId ? `/purchases/${purchaseId}/${itemId}` : `/purchases/${purchaseId}`;

        history.push(url);
    };

    return isPartnerTransaction ? (
        <div>{value}</div>
    ) : (
        <div className="hover:cursor-pointer hover:text-blue hover:underline" onClick={handleClick}>
            {value}
        </div>
    );
};

export const CouponRenderer = ({ value }: { value: Coupon | { id: string } | string }) => {
    const id = typeof value === "string" ? value : value?.id;

    const { data: coupon } = useResource<Coupon>(
        ["coupons", id],
        { source: "xola" },
        {
            enabled: !!value && !isSelectAll(value),
        },
    );
    if (isSelectAll(value)) {
        return value;
    }

    if (coupon?.name) {
        return coupon.name;
    }

    if (typeof value === "string") {
        return value;
    }

    return value?.id ?? "";
};

export const columnTypes: { [key: string]: ColDef } = {
    objectid: {
        minWidth: 240,
        maxWidth: 260,
    },
    number: {
        filter: "agNumberColumnFilter",
        headerClass: "justify-end",
        cellClass: "text-right",
        valueFormatter: (params: ValueFormatterParams) => round(params.value, 0).toString(),
    },
    currency: {
        filter: "agNumberColumnFilter",
        headerClass: "justify-end",
        cellClass: "text-right",
        cellRenderer: ({ value }: { value: string }) => {
            const currencyValue = value ?? 0;
            const className = Number(currencyValue) < 0 ? "text-danger" : "";
            return (
                <span className={clsx(className, "tabular-nums")}>
                    <Currency shouldRemoveTrailingZeroes={false}>{currencyValue}</Currency>
                </span>
            );
        },
    },
    datetime: {
        minWidth: 190,
        filter: DateRangeFilter,
        valueFormatter: (params: ValueFormatterParams) => {
            return params.value ? formatDate(params.value, DateFormat.DATE_TIME_SHORT) : "";
        },
    },
    date: {
        minWidth: 120,
        filter: DateRangeFilter,
        valueFormatter: (params: ValueFormatterParams) => {
            const field = params.colDef.field ?? "arrivalDateTime";
            const value = params.data[field];
            return isEmpty(value) ? "" : formatDate(value, DateFormat.DATE_SHORT);
        },
    },
    arrivalTime: {
        minWidth: 120,
        valueFormatter: ({ data }: { data: ExperiencePurchaseItem }) => {
            if (data.arrivalTime && data.arrivalDateTime) {
                return formatDate(data.arrivalDateTime, DateFormat.TIME);
            }

            if (data.arrivalTime) {
                return formatTime(data.arrivalTime, DateFormat.TIME);
            }

            return "";
        },
    },
    multipleDateTime: {
        minWidth: 190,
        filter: DateRangeFilter,
        cellRenderer: ({ data: transaction }: { data: Transaction }) => {
            const item = getItemForTransaction(transaction);
            if (!item) {
                return "";
            }

            const firstArrivalDate = "arrivalDate" in item ? formatDate(item.arrivalDate, DateFormat.DATE_SHORT) : "";
            if (!firstArrivalDate) {
                return "";
            }

            // Extract for re-use for products too
            const dates: string[] = [];
            const allSameDates = transaction.items.every((transactionItem_: TransactionItem, index: number) => {
                const item = getItemForTransaction(transaction, index);
                if (item) {
                    const date = "arrivalDate" in item ? formatDate(item.arrivalDate, DateFormat.DATE_SHORT) : "N/A";
                    dates.push(`${item?.name}: ${date}`);
                    return date === firstArrivalDate;
                }

                return true;
            });

            // If each item has a different date then show a generic string with dates in the tooltip
            if (dates.length > 0 && !allSameDates) {
                const tooltip = dates.map((date: string) => (
                    <div key={date} className="text-gray-light">
                        {date}
                    </div>
                ));
                return (
                    <Tooltip content={tooltip}>
                        <span className="font-bold">Multiple Dates</span>
                    </Tooltip>
                );
            }

            return firstArrivalDate;
        },
    },
    product: {
        minWidth: 250,
        cellRenderer: ProductRenderer,
        filterParams: {
            cellRenderer: ProductRenderer,
        },
    },
    purchase: {
        cellRenderer: PurchaseRenderer,
    },
    user: {
        filter: "agTextColumnFilter",
        cellRenderer: UserRenderer,
        keyCreator: (params: ValueFormatterParams) => params.value.id ?? params.value,
        valueFormatter: (params: ValueFormatterParams) => params.value.id,
        valueGetter: (params: ValueGetterParams) => {
            // So that if user copies the cell value they get the ID instead of an [Object object]
            return get(params.data, `${params.colDef.field}.id`) ?? "";
        },
        filterParams: {
            cellRenderer: UserRenderer,
        },
    },
    tags: {
        valueGetter: (params: ValueGetterParams) => {
            if (params.data?.type === TransactionType.STRIPE_PAYDOWN) {
                return "Stripe Debt Payment";
            }
            // Using `-` instead of blank strings to allow filtering of items without tags
            return params.data?.purchase?.tags?.map((tag: Tag) => tag.id).join(", ") || "-";
        },
    },
    source: {
        filter: "agSetColumnFilter",
        valueGetter: (params: ValueGetterParams) => params.data.source ?? Source.OFFICE,
        cellRenderer: SourceCellRenderer,
        minWidth: 150,
        filterParams: {
            cellRenderer: SourceCellRenderer,
            values: (params: SetFilterValuesFuncParams) => {
                const installations = getCollection<Installation>("installations");
                const bookingEngines: string[] = getCollection<Plugin>("plugins")
                    .filter((plugin) => {
                        const isBookingEngine = plugin.permissions?.includes(DelegateRole.BOOKING_ENGINE);
                        const isInstalled = installations.some((installation) => installation.plugin === plugin.id);
                        return isBookingEngine && isInstalled;
                    })
                    .map((plugin) => {
                        const isBookingEngineNamedSource = Object.values(BookingEngineNamedSourceCopy).includes(
                            plugin.name,
                        );
                        return isBookingEngineNamedSource
                            ? // To pass saved Purchase Source name of these Booking engines in Source filter query instead of Plugin id
                              invert(BookingEngineNamedSourceCopy)[plugin.name]
                            : plugin.id;
                    });
                params.success(union([...transactionSources, ...bookingEngines]));
            },
        },
    },
    startCase: {
        valueFormatter: (params: ValueFormatterParams) => startCase(params.value),
    },
    firstElement: {
        valueGetter: (params: ValueGetterParams) => {
            const [field, ...path] = params.colDef.field?.split(".") ?? [];
            return get(params.data, [field, 0, ...path]);
        },
    },
};

export const MultipleArrivalDateRenderer = ({ dates }: { dates: string[] }) => {
    const tooltip = dates.map((date) => {
        return (
            <NoTranslate key={date} as="div" className="text-gray-light">
                {formatDate(date, DateFormat.DATE_SHORT)}
            </NoTranslate>
        );
    });

    return <Tooltip content={tooltip}>Multiple Dates</Tooltip>;
};

export const MultipleArrivalTimeRenderer = ({ times }: { times: string[] }) => {
    if (times.length === 1) {
        return formatTime(times[0], DateFormat.TIME);
    }

    const tooltip = times.map((time) => (
        <div key={time} className="text-gray-light">
            {formatTime(time, DateFormat.TIME)}
        </div>
    ));

    return <Tooltip content={tooltip}>Multiple Times</Tooltip>;
};

export const MultipleProductNameRenderer = ({ value }: { value: string }) => {
    const { products: activeProducts } = useProducts();
    const { deletedExperiences } = useDeletedExperiences();

    const products = useMemo(() => {
        return concat(activeProducts, deletedExperiences);
    }, [activeProducts, deletedExperiences]);

    if (isSelectAll(value)) {
        return value;
    }

    const productIds = value.split(",");
    const names = products.filter((product) => productIds.includes(product.id)).map((item) => capitalize(item.name));
    const partnerProductLabel = productIds.find((element) => element.startsWith("Partner")) ?? "";
    if (partnerProductLabel) {
        names.push(partnerProductLabel);
    }

    const tooltip = <Tooltip content={names.join(", ")}>Multiple Products</Tooltip>;
    return names.length > 1 ? tooltip : names[0] ?? "";
};

export const PartnerRenderer = ({ value }: { value: Partner | { id: string } | string }) => {
    const isEmpty = value === "empty";
    const id = typeof value === "string" ? value : value?.id;
    const { data: partner } = useResource<Partner>(
        ["partners", id],
        { expand: "partnerSeller" },
        { enabled: !!value && !isSelectAll(value) && !isEmpty },
    );

    if (isEmpty) {
        return "Empty";
    }

    if (isSelectAll(value)) {
        return value;
    }

    if (partner?.name) {
        return partner.name;
    }

    if (typeof value === "string") {
        return value;
    }

    return id;
};

const getPurchaseId = (data: Transaction | Purchase | ExperiencePurchaseItem | TicketRedemption) => {
    if ("purchase" in data && data.purchase) {
        return data.purchase.id;
    }

    if ("order" in data) {
        return data.order;
    }

    return data.id;
};

const getPurchaseItemId = (data: Purchase | ExperiencePurchaseItem | TicketRedemption) => {
    if ("object" in data && data.object === PurchaseItemObject.EXPERIENCE) {
        return data.id;
    }

    if ("item" in data) {
        return data.item;
    }

    return null;
};
